import React, { useCallback, useMemo } from "react";
import { Modal } from "react-bootstrap";
import { Calendar, X, Plus, Trash2, Bell } from "lucide-react";
import { Formik, Form, Field, ErrorMessage, FieldArray } from "formik";
import * as Yup from "yup";
import { getApiErrorMessage } from "../../../../../utils/utils";

import { updateCalendarEvent } from "../core/request";
import { IEvent, IReminder } from "../core/type";
import { useSnackbar } from "../../../../../_theme/context/SnackbarContext";
import { REMINDER_UNITS } from "../core/data";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../../../redux/store";
import { updateEventAction } from "../../../../../redux/actions/events.actions";



interface AddorUpdateModalProps {
    onHide: () => void;
    event: IEvent | null;
    setSelectedEvent: React.Dispatch<React.SetStateAction<IEvent | null>>;
    setEventsForModal: React.Dispatch<React.SetStateAction<IEvent[]>>;
    events: IEvent[]
}

const createReminder = (): IReminder => ({
    id: crypto.randomUUID(),
    value: 15,
    unit: "hours",
});

const REMINDER_LIMITS = {
    minutes: { min: 1, max: 59 },
    hours: { min: 1, max: 23 },
    days: { min: 1, max: 365 },
    weeks: { min: 1, max: 52 },
    months: { min: 1, max: 12 },
} as const;

const SetReminderPopup: React.FC<AddorUpdateModalProps> = React.memo(
    ({ onHide, event, setSelectedEvent, setEventsForModal, events }) => {
        const { showSnackbar } = useSnackbar();
        const dispatch = useDispatch<AppDispatch>();
        const validationSchema = useMemo(
            () =>
                Yup.object({
                    reminders: Yup.array()
                        .of(
                            Yup.object({
                                value: Yup.number()
                                    .typeError("Value is required")
                                    .required("Value is required")
                                    .when("unit", ([unit], schema) => {
                                        const limit =
                                            REMINDER_LIMITS[
                                            unit as keyof typeof REMINDER_LIMITS
                                            ];

                                        return limit
                                            ? schema
                                                .min(
                                                    limit.min,
                                                    `${unit} must be between ${limit.min} and ${limit.max}`
                                                )
                                                .max(
                                                    limit.max,
                                                    `${unit} must be between ${limit.min} and ${limit.max}`
                                                )
                                            : schema;
                                    }),


                                unit: Yup.string().required("Unit is required"),
                            })
                        )
                        .test(
                            "unique-reminders",
                            "Duplicate reminders are not allowed",
                            (reminders) => {
                                if (!reminders) return true;

                                const seen = new Set<string>();

                                for (const reminder of reminders) {
                                    const key = `${reminder.value}-${reminder.unit}`;

                                    if (seen.has(key)) {
                                        return false;
                                    }

                                    seen.add(key);
                                }

                                return true;
                            }
                        ),
                }),
            []
        );

        const initialValues = useMemo(
            () => ({
                reminders: event?.my_personal_reminders || [] as IReminder[],
            }),
            [event]
        );

        const getReminderLimits = useCallback((unit: string) => {
            const limits = {
                minutes: 59,
                hours: 23,
                days: 365,
                weeks: 52,
                months: 12,
            };
            return limits[unit as keyof typeof limits] || 365;
        }, []);

        const handleSubmit = useCallback(
            async (
                values: { reminders: IReminder[] },
                {
                    setSubmitting,
                }: {
                    setSubmitting: (isSubmitting: boolean) => void;
                }
            ) => {
                if (!event) return;

                setSubmitting(true);
                try {
                    const response = await updateCalendarEvent({
                        event_id: event.event_id,
                        reminders: values.reminders || [],
                        personal_reminders_on_global: 1,
                    });

                    if (response.status === "ok") {
                        const data = { ...event, my_personal_reminders: values.reminders, personal_reminders_on_global: true };
                        setSelectedEvent(data);
                        setEventsForModal((prev) =>
                            prev.map((item) =>
                                item.id === data.id ? data : item
                            )
                        );
                        onHide()
                        dispatch(updateEventAction(data))
                        showSnackbar(response.message, "success");
                    } else {
                        showSnackbar(response.message, "danger");
                    }
                } catch (error) {
                    console.error("Error saving reminders:", error);
                    showSnackbar(getApiErrorMessage(error, "Failed to save reminders"), "danger");
                } finally {
                    setSubmitting(false);
                }
            },
            [event, onHide, showSnackbar]
        );

        return (

            <div className="bg-white rounded-4 overflow-hidden">
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    enableReinitialize
                    validateOnChange
                    onSubmit={handleSubmit}
                >
                    {({
                        values,
                        isSubmitting,
                        errors,
                    }) => (
                        <Form noValidate>
                            {/* Reminders Section */}
                            <div
                                className="mb-4 p-3 rounded-3"
                            >
                                <FieldArray name="reminders">
                                    {({ push, remove }) => (
                                        <>
                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                <h6 className="fw-semibold mb-0">
                                                    <Bell
                                                        size={16}
                                                        className="me-1 text-primary"
                                                    />
                                                    Reminders
                                                </h6>

                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-light-info d-flex align-items-center gap-1"
                                                    onClick={() =>
                                                        push(createReminder())
                                                    }
                                                >
                                                    <Plus size={14} />
                                                    Add Reminder
                                                </button>
                                            </div>

                                            {values.reminders.length > 0 ? (
                                                <>
                                                    {values.reminders.map(
                                                        (reminder, index) => (
                                                            <div
                                                                key={reminder.id}
                                                                className="mb-3"
                                                            >
                                                                <div className="row g-2 align-items-end">
                                                                    <div className="col-md-5">
                                                                        <label className="form-label fw-semibold small mb-1">
                                                                            Value
                                                                        </label>

                                                                        <Field
                                                                            type="number"
                                                                            name={`reminders.${index}.value`}
                                                                            className="form-control"
                                                                            placeholder="e.g. 15"
                                                                            min={1}
                                                                            max={getReminderLimits(
                                                                                reminder.unit
                                                                            )}
                                                                        />

                                                                        <ErrorMessage
                                                                            name={`reminders.${index}.value`}
                                                                            component="div"
                                                                            className="text-danger small mt-1"
                                                                        />
                                                                    </div>

                                                                    <div className="col-md-5">
                                                                        <label className="form-label fw-semibold small mb-1">
                                                                            Unit
                                                                        </label>

                                                                        <Field
                                                                            as="select"
                                                                            name={`reminders.${index}.unit`}
                                                                            className="form-select"
                                                                        >
                                                                            {REMINDER_UNITS.map(
                                                                                (unit) => (
                                                                                    <option
                                                                                        key={
                                                                                            unit.value
                                                                                        }
                                                                                        value={
                                                                                            unit.value
                                                                                        }
                                                                                    >
                                                                                        {unit.label}
                                                                                    </option>
                                                                                )
                                                                            )}
                                                                        </Field>

                                                                        <ErrorMessage
                                                                            name={`reminders.${index}.unit`}
                                                                            component="div"
                                                                            className="text-danger small mt-1"
                                                                        />
                                                                    </div>

                                                                    <div className="col-md-1">
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-icon btn-light-danger w-100"
                                                                            onClick={() =>
                                                                                remove(index)
                                                                            }
                                                                            aria-label="Remove reminder"
                                                                        >
                                                                            <Trash2 size={14} />
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )
                                                    )}
                                                    {typeof errors.reminders ===
                                                        "string" && (
                                                            <div className="text-danger small mt-2">
                                                                {errors.reminders}
                                                            </div>
                                                        )}

                                                    <div
                                                        className="mt-3 p-2 rounded-3"
                                                        style={{
                                                            backgroundColor:
                                                                "#e7f3ff",
                                                        }}
                                                    >
                                                        <small className="d-flex align-items-center gap-2 text-primary">
                                                            <Bell size={12} />
                                                            You will be notified
                                                            before the event starts
                                                            based on these reminders.
                                                        </small>
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="text-center py-3 text-muted">
                                                    <Bell
                                                        size={32}
                                                        className="mb-2 opacity-50"
                                                    />

                                                    <p className="mb-0 small">
                                                        No reminders set
                                                    </p>

                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-link mt-2"
                                                        onClick={() =>
                                                            push(createReminder())
                                                        }
                                                    >
                                                        Add your first reminder
                                                    </button>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </FieldArray>
                            </div>

                            {/* Footer */}
                            <div className="d-flex gap-2  justify-content-end p-3 border-top">
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary px-4"
                                    onClick={onHide}
                                    disabled={isSubmitting}
                                >
                                    Close
                                </button>

                                <button
                                    type="submit"
                                    className="btn btn-primary px-4"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" />
                                            Saving...
                                        </>
                                    ) : (
                                        "Set Reminder"
                                    )}
                                </button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        );
    }
);

SetReminderPopup.displayName = "SetReminderPopup";

export default SetReminderPopup;
